import { formatCalendarDate } from '../../lib/format';
import type { TicketListItem } from '../../api/types';
import { TicketMiniList } from './TicketMiniList';
import {
  ARCHIVE_NOTE,
  actionLabel,
  actionsFor,
  unarchiveDestination,
  type VersionAction,
} from './version-actions';
import { describeCount, type VersionRollup } from './version-rollup';
import { isKnownState, type Version } from './versions-api';

/**
 * One version, with its TWO ticket relationships shown SEPARATELY.
 *
 * 🔴 They are different questions and users conflate them:
 *   · **Planned for** — `ticket.version_id`. One version per ticket. What was scheduled for it.
 *   · **Shipped in**  — `ticket_fix_version`. MANY versions per ticket. Where the work landed.
 * ⛔ There is deliberately no combined total. Adding them double-counts everything that is in both,
 * and a single "24 tickets" would hide the fact that eight of them slipped.
 */
export function VersionCard({
  rollup,
  windowIsComplete,
  busy,
  error,
  onAction,
  onOpenTicket,
}: {
  rollup: VersionRollup;
  /** Whether the ticket window covered the whole project — only matters if the API sent no totals. */
  windowIsComplete: boolean;
  /** A request for THIS version is in flight. ⭐ Per row, not per page — see `useVersionActions`. */
  busy: boolean;
  /** The API's refusal for this row, verbatim. Dialog-less actions have nowhere else to show one. */
  error: string | null;
  onAction: (action: VersionAction, version: Version) => void;
  onOpenTicket: (ticket: TicketListItem) => void;
}) {
  const { version, plannedIn, fixedIn, inBothCount } = rollup;
  const planned = describeCount(rollup.plannedTotal, plannedIn.length, windowIsComplete);
  const fixed = describeCount(rollup.fixedTotal, fixedIn.length, windowIsComplete);
  const actions = actionsFor(version.state);

  return (
    <article className="rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] p-3">
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{version.name}</h3>
        <StateChip state={version.state} />
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          {version.state === 'released'
            ? version.release_date
              ? `Released ${formatCalendarDate(version.release_date)}`
              : /* ⚠️ Released with no date is a fact about the DATA, not an unreleased version. */
                'Released — no release date recorded'
            : version.release_date
              ? `Planned for ${formatCalendarDate(version.release_date)}`
              : 'No date set'}
        </span>

        {/**
         * 🔴 PH-28's CONTROLS. ⭐ `ml-auto` and a 44px touch target: on a 390px card this row wraps to
         * its own line, which is where a consequential button belongs — not squeezed beside a chip.
         * ⛔ Only the actions this STATE offers, so no button's sole possible outcome is a 409 — see
         * `actionsFor`, which reads the API's refusals rather than guessing them.
         */}
        {actions.length > 0 ? (
          <span className="ml-auto flex flex-wrap gap-2">
            {actions.map((action) => (
              <button
                key={action}
                type="button"
                disabled={busy}
                onClick={() => onAction(action, version)}
                title={
                  action === 'archive'
                    ? ARCHIVE_NOTE
                    : action === 'unarchive'
                      ? `Returns it to ${unarchiveDestination(version)} — derived from its release date, not remembered.`
                      : 'Asks for confirmation first — releasing cannot be undone.'
                }
                className={`h-11 rounded px-3 text-sm font-semibold disabled:opacity-50 sm:h-8 ${
                  action === 'release'
                    ? 'bg-[color:var(--color-accent)] text-[color:var(--color-on-accent)]'
                    : 'border border-[color:var(--color-line)]'
                }`}
              >
                {busy ? '…' : actionLabel(action)}
              </button>
            ))}
          </span>
        ) : null}
      </header>

      {/* ⛔ VERBATIM, and on the row that caused it. `version 'x' is already archived` tells the
          reader somebody else got there first; a page-level "Could not archive" does not. */}
      {error ? (
        <p role="alert" className="mt-2 text-xs font-semibold text-red-700 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {version.description ? (
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">{version.description}</p>
      ) : null}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <TicketGroup
          title="Planned for this version"
          hint="ticket.version — one per ticket"
          tickets={plannedIn}
          count={planned}
          emptyText="No ticket names this as its version."
          onOpenTicket={onOpenTicket}
        />
        <TicketGroup
          title="Shipped in this version"
          hint="fix version — a ticket can ship in several"
          tickets={fixedIn}
          count={fixed}
          emptyText="No ticket lists this as a fix version."
          onOpenTicket={onOpenTicket}
        />
      </div>

      {inBothCount > 0 ? (
        <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
          ⭐ {inBothCount} ticket{inBothCount === 1 ? ' is' : 's are'} in both lists — planned for this
          version and shipped in it. ⛔ The two counts above therefore do not add up to a total.
        </p>
      ) : null}
    </article>
  );
}

function TicketGroup({
  title,
  hint,
  tickets,
  count,
  emptyText,
  onOpenTicket,
}: {
  title: string;
  hint: string;
  tickets: readonly TicketListItem[];
  count: ReturnType<typeof describeCount>;
  emptyText: string;
  onOpenTicket: (ticket: TicketListItem) => void;
}) {
  return (
    <section className="min-w-0">
      <h4 className="flex items-baseline gap-1.5 text-xs font-bold tracking-wide uppercase">
        {title}
        <span
          title={
            count.isExact
              ? 'Counted by the API over the whole project'
              : 'At least this many — counted over the tickets this page could load'
          }
          className="rounded bg-[color:var(--color-line)] px-1 text-[10px]"
        >
          {count.text}
        </span>
      </h4>
      <p className="text-[11px] text-[color:var(--color-ink-muted)]">{hint}</p>

      {/* ⭐ The API counted the whole project; the LIST is only what this page loaded. Saying so is
          the honest reconciliation — printing the shorter number would contradict the badge, and
          printing nothing would make the badge look wrong. */}
      {count.hiddenFromList > 0 ? (
        <p className="mt-1 text-[11px] text-[color:var(--color-ink-muted)]">
          Listing {tickets.length} of {count.text} — the other {count.hiddenFromList} are outside the
          tickets this page loaded.
        </p>
      ) : null}

      {tickets.length === 0 ? (
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          {count.isExact && count.hiddenFromList > 0
            ? 'None of them are in the tickets this page loaded.'
            : emptyText}
        </p>
      ) : (
        <TicketMiniList tickets={tickets} onOpenTicket={onOpenTicket} />
      )}
    </section>
  );
}

export function StateChip({ state }: { state: string }) {
  // ⚠️ An unknown state is shown as itself. Bucketing it into "unreleased" would report a shipped
  // release as unshipped, which is the one direction that matters.
  const tone = !isKnownState(state)
    ? 'bg-amber-200 text-amber-950'
    : state === 'released'
      ? 'bg-emerald-200 text-emerald-950'
      : state === 'archived'
        ? 'bg-slate-300 text-slate-900'
        : 'bg-sky-200 text-sky-950';

  return (
    <span className={`rounded-[3px] px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase ${tone}`}>
      {state}
      {isKnownState(state) ? '' : ' ?'}
    </span>
  );
}
